/**
 * `/homework/:uploadId/feedback` (#377, U1): what a learner gets back from a homework check.
 *
 * The order is the one principle 10 sets (`docs/feedback_research.md` §8): **a summary on top**, one entry per question -
 * what was proved, the kind of gap and where, and what was read but not checked; then **each page with its notes marked
 * on it**; and **the per-question table last**.
 *
 * **Showing is recording.** `GET .../feedback` records nothing, so it shows no verdict (every checked line is `on_request`,
 * alike whatever its verdict). Arriving at a checked upload makes the check POST by itself, once, while the page is
 * visible; that POST records every rung it shows before it answers (#406), and nothing from the run is drawn, nor the
 * feedback channel opened, until it answers. What it showed is kept in memory only, so a reload POSTs again and that
 * showing is recorded too. A rung pull opens one line; only that line's note is taken from its response (`withPulledNote`).
 *
 * Before the due date the server offers the hint (and a counterexample where one is certified); the solution waits for
 * the due date and stays closed while it is unknown. That rule is #367's and the server's: this screen shows the rungs a
 * response delivered and offers only the ones it lists as pullable, and decides nothing about either.
 *
 * **Beside the page (#660).** The ✗ notes come first in the notes column, then the lines the reviewer flagged, then the rest, and the
 * lines read and not checked are folded into one closed row at the end. "Post a fixed version" is offered only on a re-check, which
 * the sheet list says: an earlier checked upload of the same exercise in the same course. When the question sheet is missing the
 * channel says so (`no_question_sheet`) and that card sits at the top of the notes column.
 *
 * **Confidence is asked once per question, before the first check, and nowhere else (#438 slice 2, HYP-UX-005; H3 in
 * `docs/feedback_research.md`).** Every question is asked, not only the ones that turn out wrong, because asking only about
 * a question after (or because of) its verdict would let the question itself reveal the verdict; and it is asked only while
 * no check run exists, so the answer provably precedes every verdict (the server and the database both refuse a later one).
 * The three choices are skippable and a skip is sent as `skipped`, because HYP-UX-005 is falsified by skipping. The answers
 * are the learner's own word, recorded as an observation and shown back nowhere: never a status, a score or a count.
 * On a revised upload of an exercise already checked the prompt is not shown, because the server would refuse the answers
 * (it says so in `selfReportOpen`). It is asked one question at a time, beside the learner's own page with that question's lines
 * framed (`confidence-prompt.tsx`), with no verdict anywhere on the screen: the same condition as before (`run === null`) keeps the marks
 * out, and the page is drawn with no note at all.
 *
 * No status, score, count or percentage (`no-percentage.test.tsx`), and no word about what the learner understands:
 * homework is not observed (D11), so the page is about the work.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { HomeworkFeedbackResponse } from '@davinci/contracts';
import { ApiRequestError } from '../../api/client.js';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { useFocusFollows } from '../../app/course-focus.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { usageMessages, useModelLocked } from '../usage/usage-meter.js';
import { fillTemplate } from '../../i18n/fill-template.js';
import {
  MARK_SYMBOLS,
  useFeedbackLabels,
  type FeedbackLabels,
  type PullableRung,
} from '../../i18n/homework-feedback-labels.js';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { localizedNoteSentence } from '../../i18n/note-sentences.js';
import { feedbackChrome } from './feedback-chrome.js';
import { ConfidenceFlow, confidenceAnswers, type ConfidenceChoices, type ConfidencePage } from './confidence-prompt.js';
import { FeedbackNoteCard } from './feedback-note.js';
import { fixedEntry, FixedMark, type FixedEntry } from './fixed-mark.js';
import { SubmissionRules } from './submission-rules.js';
import { useIsPhone } from '../../app/use-is-phone.js';
import { FeedbackChannel, WithoutSheetNote, type ChannelSummary } from './feedback-channel.js';
import { buildBoardNotes } from './feedback-blocks.js';
import { TriageList } from './triage-list.js';
import { FeedbackPages, SummaryChips } from './feedback-pages.js';
import { isClosed, nothingDecided, summarise, topicLine, withDisplayedHints, withDisputedNote, withMovedNote, withPulledNote, type FeedbackNote, type QuestionSummary } from './feedback-model.js';
import { UntrustedText } from './untrusted-text.js';

function refusalSentence(error: unknown, t: FeedbackLabels): string | undefined {
  if (!(error instanceof ApiRequestError)) return undefined;
  return t.failureSentence(error.code) || undefined;
}

export function HomeworkFeedbackPage(): React.JSX.Element {
  const { uploadId = '' } = useParams();
  // Keyed by upload, so moving between uploads never carries the shown response or the fired guard across.
  return <HomeworkFeedbackBody key={uploadId} uploadId={uploadId} />;
}

function HomeworkFeedbackBody({ uploadId }: { readonly uploadId: string }): React.JSX.Element {
  const t = useFeedbackLabels();
  const chrome = useMessages(feedbackChrome);
  const h = useHomeworkLabels();
  const { locale } = useLocale();
  const api = useApi();
  const queryClient = useQueryClient();
  const locked = useModelLocked();
  const usage = useMessages(usageMessages);
  // What the last POST showed. Memory only: nothing from it is cached, so a reload POSTs again (see the file comment).
  const [opened, setOpened] = useState<HomeworkFeedbackResponse | undefined>(undefined);

  const upload = useQuery({ queryKey: queryKeys.homeworkUpload(uploadId), queryFn: () => api.getHomework(uploadId) });
  const closed = useQuery({
    queryKey: queryKeys.homeworkFeedback(uploadId),
    queryFn: () => api.getHomeworkFeedback(uploadId),
    enabled: opened === undefined,
  });

  // Triage (#469): worked out from the confirmed transcription before the check; it never blocks the check.
  const triage = useQuery({
    queryKey: queryKeys.homeworkTriage(uploadId),
    queryFn: () => api.triageHomework(uploadId, crypto.randomUUID()),
    enabled: closed.data?.run === null && opened === undefined,
    retry: false,
    staleTime: Infinity,
  });

  // Confidence (#438 slice 2): the answers so far, and whether the server already has them (a retried check does not resend).
  const [choices, setChoices] = useState<ConfidenceChoices>({});
  // What the channel shows the page (#660): the flagged lines, and whether the question sheet is missing; and where the latter's card goes.
  const [channelSummary, setChannelSummary] = useState<ChannelSummary>({ flaggedRegionIds: [], noSheet: false, withoutSheet: false, flags: [], reasons: {}, why: () => undefined });
  const [leadSlot, setLeadSlot] = useState<HTMLElement | null>(null);
  const confidenceSent = useRef(false);
  const arrivalPosted = useRef(false);
  const open = useMutation({
    mutationFn: async (labels: readonly (string | null)[]) => {
      if (labels.length > 0 && !confidenceSent.current) {
        try {
          await api.recordHomeworkConfidence(uploadId, crypto.randomUUID(), { answers: confidenceAnswers(labels, choices) });
          confidenceSent.current = true;
        } catch (error) {
          // A check run already exists, so the question is moot and the check proceeds; anything else stops before the check.
          if (!(error instanceof ApiRequestError && error.code === 'confidence_after_verdict')) throw error;
        }
      }
      return api.checkHomework(uploadId, crypto.randomUUID());
    },
    onSuccess: (response) => {
      setOpened(response);
      // The read from before this POST may still say no check ran. The next arrival reads again rather than asking how sure a second time.
      queryClient.removeQueries({ queryKey: queryKeys.homeworkFeedback(uploadId), exact: true });
    },
  });
  const pull = useMutation({
    mutationFn: (input: { claimId: string; rung: PullableRung }) =>
      api.pullHomeworkRung(input.claimId, crypto.randomUUID(), { rung: input.rung }),
    onSuccess: (response, input) => {
      arrivalPosted.current = false;
      setOpened((current) => {
        const next = current === undefined ? response : withPulledNote(current, response, input.claimId);
        // The run changed under the page, so this response closes what was on screen. Arrive again: the read, then the POST that records what it shows.
        return isClosed(next) ? undefined : next;
      });
    },
  });

  // Moving a note to another question (#668). Nothing changes on screen before the 200, and the response patches that one note in place:
  // the page is showing a verdict, so it is never re-read and never re-opened. A solution already shown keeps saying which question it was written for (its own `solutionQuestion`).
  const [moveError, setMoveError] = useState<unknown>(undefined);
  const moveNote = async (claimId: string, label: string): Promise<boolean> => {
    setMoveError(undefined);
    try {
      const moved = await api.moveHomeworkNoteQuestion(uploadId, claimId, crypto.randomUUID(), { label });
      setOpened((current) => (current === undefined ? current : withMovedNote(current, claimId, moved.questionHint, moved.pullable)));
      return true;
    } catch (error) {
      setMoveError(error);
      return false;
    }
  };

  // Flagging a verdict (#438 slice 3). Nothing changes on screen before the 201; a dispute already filed is re-read from the server.
  const [disputeError, setDisputeError] = useState<unknown>(undefined);
  const dispute = async (claimId: string): Promise<void> => {
    setDisputeError(undefined);
    try {
      const filed = await api.disputeHomeworkVerdict(uploadId, claimId, crypto.randomUUID());
      setOpened((current) => (current === undefined ? current : withDisputedNote(current, claimId, filed.ruleBy, t.underReviewText)));
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === 'dispute_already_filed') {
        try {
          setOpened(await api.checkHomework(uploadId, crypto.randomUUID()));
        } catch (refetchError) {
          setDisputeError(refetchError);
        }
      } else setDisputeError(error);
    }
  };

  const feedback = withDisplayedHints(opened ?? closed.data);

  // Arriving on a checked upload makes the POST by itself, once, and only while the page is visible and can be drawn: a background
  // tab, a link preview or a page that failed to load is not seeing the help. A failure is not retried here; the notice's button is the retry.
  const waitingToShow = closed.data !== undefined && upload.data !== undefined && isClosed(closed.data) && opened === undefined;
  useEffect(() => {
    if (!waitingToShow || arrivalPosted.current) return undefined;
    const post = (): void => {
      if (arrivalPosted.current) return;
      arrivalPosted.current = true;
      open.mutate([]);
    };
    if (document.visibilityState === 'visible') {
      post();
      return undefined;
    }
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') post();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `open` is a new object each render; `waitingToShow` is the trigger.
  }, [waitingToShow]);

  // A re-check (#660): the sheet list holds an earlier checked upload of this exercise in this course. Only then is "Post a fixed version" offered.
  const sheetList = useQuery({ queryKey: queryKeys.homeworkList, queryFn: () => api.listHomework(), enabled: feedback?.run != null });
  const own = sheetList.data?.uploads.find((entry) => entry.uploadId === uploadId);
  const isRecheck =
    own !== undefined &&
    own.exerciseId !== undefined &&
    (sheetList.data?.uploads ?? []).some(
      (entry) => entry.uploadId !== uploadId && entry.courseRef === own.courseRef && entry.exerciseId === own.exerciseId && entry.checked && Date.parse(entry.createdAt) < Date.parse(own.createdAt),
    );

  // The small "COURSE · due …" line above the title (#556): from data the web already reads (the sheet list and the course list).
  // Shown only once the check names its exercise, and only when that exercise id is unambiguous across the courses.
  const exerciseId = feedback?.run?.exerciseId ?? null;
  const sheets = useQuery({ queryKey: queryKeys.homeworkExercises, queryFn: () => api.homeworkExercises(), enabled: exerciseId !== null });
  const courseList = useQuery({ queryKey: queryKeys.courses, queryFn: () => api.getCourses(), enabled: exerciseId !== null });
  const matches = (sheets.data?.exercises ?? []).filter((sheet) => sheet.exerciseId === exerciseId);
  const sheet = matches.length === 1 ? matches[0] : undefined;
  useFocusFollows(sheet?.courseRef);
  const sheetCourse = sheet === undefined ? undefined : courseList.data?.courses.find((course) => course.moodleCourseId === sheet.courseRef);
  const eyebrow =
    sheet !== undefined && sheetCourse !== undefined ? (
      <p className="feedback-eyebrow">
        <bdi dir="auto">{sheetCourse.code}</bdi> · {h.dueOn(h.dueDate(sheet.dueAt))}
      </p>
    ) : null;
  const pages = upload.data?.pages ?? [];
  // The questions to ask about: each distinct question label of the sheet, in page order; the sheet as a whole (null) when
  // none is labelled or the transcription cannot be read. Only before the first check (no run yet, and the server still accepts the answers).
  const firstCheck = feedback !== undefined && feedback.run === null && feedback.selfReportOpen;
  const transcriptions = useQueries({
    queries: [...pages]
      .sort((a, b) => a.pageIndex - b.pageIndex)
      .map((page) => ({
        queryKey: queryKeys.homeworkTranscription(page.pageId),
        queryFn: () => api.getHomeworkTranscription(page.pageId),
        enabled: firstCheck,
        retry: false,
      })),
  });
  const labelsSettled = firstCheck && upload.data !== undefined && transcriptions.every((q) => !q.isPending);
  const distinctLabels = [
    ...new Set(transcriptions.flatMap((q) => (q.data?.regions ?? []).map((region) => region.questionLabel)).filter((label): label is string => label !== null)),
  ];
  const confidenceLabels: readonly (string | null)[] = !labelsSettled ? [] : distinctLabels.length > 0 ? distinctLabels : [null];
  const sortedPages = [...pages].sort((a, b) => a.pageIndex - b.pageIndex);
  // The pages as the confidence step draws them: each with its lines' question labels and boxes (a page whose lines could not be read has none).
  const confidencePages: ConfidencePage[] = sortedPages.map((page, index) => ({
    pageId: page.pageId,
    pageNumber: page.pageIndex + 1,
    widthPx: page.widthPx,
    heightPx: page.heightPx,
    lines: (transcriptions[index]?.data?.regions ?? []).map((region) => ({ questionLabel: region.questionLabel, bbox: region.bbox })),
  }));
  const pageOf = (pageId: string) => pages.find((page) => page.pageId === pageId);
  const pageNumberOf = (pageId: string): number => (pageOf(pageId)?.pageIndex ?? 0) + 1;

  if (feedback === undefined || upload.data === undefined) {
    const error = closed.error ?? upload.error;
    return (
      <div className="page homework feedback-page">
        <h1>{t.feedbackPageHeading}</h1>
        {error !== null ? (
          <FailureNotice error={error} onRetry={() => void (closed.isError ? closed.refetch() : upload.refetch())} />
        ) : (
          <p role="status">{t.loadingFeedback}</p>
        )}
      </div>
    );
  }

  const run = feedback.run;
  const showing = run !== null && !isClosed(feedback);
  // A checked upload that is not showing yet: the arrival POST is in flight, not yet sent, or failed.
  const arriving = run !== null && !showing;
  const openRefusal = open.isError ? refusalSentence(open.error, t) : undefined;
  const pullRefusal = pull.isError ? refusalSentence(pull.error, t) : undefined;
  // What follows the button that opens the feedback: the rules-only note, the wait, and a refusal or failure.
  const openStatus = (
    <>
      {locked ? <p className="field-hint">{usage.rulesOnlyCheck}</p> : null}
      {open.isPending && !arriving ? <p role="status">{t.checkingHomework}</p> : null}
      {open.isError ? (
        openRefusal !== undefined ? (
          <div className="notice notice-error" role="alert">
            <p>{openRefusal}</p>
          </div>
        ) : (
          <FailureNotice error={open.error} onRetry={() => open.mutate(firstCheck ? confidenceLabels : [])} />
        )
      ) : null}
    </>
  );

  // The blocks of the sheet, in reading order, each with its number: a frame on the page and its note share a name (#661).
  const boardNotes = buildBoardNotes({ notes: feedback.notes, flags: channelSummary.flags, pageNumberOf, hasPage: (pageId) => pageOf(pageId) !== undefined });
  // The table lists the response's own notes, under the number of the block each was drawn in.
  const tableEntries = boardNotes.flatMap((entry) => entry.members.map((note) => ({ number: entry.number, note })));
  // Every page the learner uploaded is there to look at, also one with no note: a page missing from the pager reads as lost.
  const pageIds = sortedPages.map((page) => page.pageId);

  return (
    <div className="page homework feedback-page">
      <div className="fb-top">
        <div className="fb-title">
          <Link className="fb-back" to={`/homework/${encodeURIComponent(uploadId)}`}>
            {t.backToPages}
          </Link>
          <div className="fb-heading">
            <h1>{sheet?.title !== undefined ? <bdi dir="auto">{sheet.title}</bdi> : t.feedbackPageHeading}</h1>
            {eyebrow}
          </div>
        </div>
        {showing ? <SummaryChips notes={feedback.notes} onlyNotCheckable={channelSummary.noSheet && nothingDecided(feedback.notes)} lookHere={channelSummary.flags.length > 0} /> : null}
      </div>

      {!showing ? (
        firstCheck && !labelsSettled ? (
          <p role="status">{t.loadingFeedback}</p>
        ) : firstCheck ? (
          <ConfidenceFlow
            labels={confidenceLabels}
            pages={confidencePages}
            onChoose={(label, choice) => setChoices((current) => ({ ...current, [label ?? '']: choice }))}
            done={
              <>
                <p>{t.openFeedbackExplanation}</p>
                {triage.data !== undefined ? <TriageList triage={triage.data} /> : null}
                <button type="button" className="fb-sure-show" disabled={open.isPending} onClick={() => open.mutate(confidenceLabels)}>
                  {chrome.sureShow}
                </button>
                {openStatus}
              </>
            }
          />
        ) : arriving ? (
          <>
            {open.isError ? null : <p role="status">{t.loadingFeedback}</p>}
            {openStatus}
          </>
        ) : (
          <section className="panel" aria-labelledby="feedback-open">
            <h2 id="feedback-open">{t.notCheckedYet}</h2>
            <p>{t.openFeedbackExplanation}</p>
            {triage.data !== undefined ? <TriageList triage={triage.data} /> : null}
            <button type="button" disabled={open.isPending} onClick={() => open.mutate([])}>
              {t.checkAction}
            </button>
            {openStatus}
          </section>
        )
      ) : null}

      {run === null || showing ? (
        <FeedbackChannel uploadId={uploadId} pages={pages} checked={run !== null} onSummary={setChannelSummary} noSheetSlot={showing ? leadSlot : null} flagsOnPage={showing} anyDecided={!nothingDecided(feedback.notes)} />
      ) : null}

      {showing ? (
        <>
          {feedback.notCheckedRegionIds.length > 0 || (nothingDecided(feedback.notes) && !channelSummary.noSheet) ? (
            <div className="notice" role="status">
              {feedback.notCheckedRegionIds.length > 0 ? <p>{t.notCheckedLines(feedback.notCheckedRegionIds.length)}</p> : null}
              {nothingDecided(feedback.notes) && !channelSummary.noSheet ? <p>{t.nothingCheckedYet}</p> : null}
            </div>
          ) : null}
          {disputeError !== undefined ? (
            refusalSentence(disputeError, t) !== undefined ? (
              <div className="notice notice-error" role="alert">
                <p>{refusalSentence(disputeError, t)}</p>
              </div>
            ) : (
              <FailureNotice error={disputeError} />
            )
          ) : null}

          {moveError !== undefined ? (
            refusalSentence(moveError, t) !== undefined ? (
              <div className="notice notice-error" role="alert">
                <p>{refusalSentence(moveError, t)}</p>
              </div>
            ) : (
              <FailureNotice error={moveError} />
            )
          ) : null}

          {pullRefusal !== undefined ? (
            <div className="notice notice-error" role="alert">
              <p>{pullRefusal}</p>
            </div>
          ) : pull.isError ? (
            <FailureNotice error={pull.error} />
          ) : null}

          {channelSummary.withoutSheet ? <WithoutSheetNote /> : null}
          <FeedbackPages
            pageIds={pageIds}
            totalPages={pages.length}
            pageNumberOf={pageNumberOf}
            pageOf={pageOf}
            notes={boardNotes}
            showPostFixed={isRecheck}
            {...(showing ? { leadRef: setLeadSlot } : {})}
            renderNote={(entry, context) => {
              const note = entry.members.length === 1 ? entry.members[0] : undefined;
              const claimId = note?.claimId ?? null;
              return (
                <FeedbackNoteCard
                  key={entry.id}
                  entry={entry}
                  run={run}
                  selected={context.selected}
                  onSelect={context.onSelect}
                  howOnly={context.howOnly ?? false} bare={context.bare ?? false}
                  {...(note !== undefined && note.mark === 'proved' && note.questionHint === undefined && fixedEntry(feedback.fixedSinceEarlier, note.questionLabel) !== undefined
                    ? { fixed: fixedEntry(feedback.fixedSinceEarlier, note.questionLabel) as FixedEntry }
                    : {})}
                  pulling={pull.isPending}
                  {...(claimId !== null ? { onMove: (label: string) => moveNote(claimId, label) } : {})}
                  {...(claimId !== null ? { onDispute: () => dispute(claimId) } : {})}
                  {...(claimId !== null ? { onPull: (rung: PullableRung) => pull.mutate({ claimId, rung }) } : {})}
                  {...(entry.flag !== undefined
                    ? { reason: channelSummary.reasons[entry.flag.regionId], onWhy: () => channelSummary.why((entry.flag as NonNullable<typeof entry.flag>).regionId) }
                    : {})}
                />
              );
            }}
          />
          <p className="fb-counts-as-help">{chrome.countsAsHelp}</p>

          <details className="fb-all">
            <summary>{chrome.allFindings}</summary>
            <Summary topic={topicLine(feedback.topic, locale)} summaries={summarise(feedback.notes, pageNumberOf)} fixed={feedback.fixedSinceEarlier} />
            <QuestionTable entries={tableEntries} pageNumberOf={pageNumberOf} />
          </details>

          {feedback.submissionRules !== null ? <SubmissionRules rules={feedback.submissionRules} /> : null}

          <details className="fb-after">
            <summary>{t.howCheckedSummary}</summary>
            <p>
              {fillTemplate(run.questionLabelPolicyVersion !== null ? t.provenanceWithQuestions : t.provenance, {
                at: run.checkedAt,
                policy: <code>{run.policyVersion}</code>,
                kernel: <code>{run.kernelVersion}</code>,
                context: <code>{run.courseContextPolicyVersion}</code>,
                digest: <code>{run.courseContextDigest.slice(0, 12)}</code>,
                questions: <code>{run.questionLabelPolicyVersion}</code>,
                location: <code>{run.locationPolicyVersion}</code>,
              })}
            </p>
          </details>
        </>
      ) : null}
    </div>
  );
}

function questionName(question: string | null, t: FeedbackLabels, questionWord: string): React.JSX.Element {
  return question === null ? (
    <>{t.noQuestionGroup}</>
  ) : (
    <>
      {questionWord} <UntrustedText text={question} />
    </>
  );
}

/** Page 1 of the feedback: one entry per question, about the work. */
function Summary({
  topic,
  summaries,
  fixed,
}: {
  readonly topic: string | null;
  readonly summaries: readonly QuestionSummary[];
  readonly fixed: HomeworkFeedbackResponse['fixedSinceEarlier'];
}): React.JSX.Element {
  const t = useFeedbackLabels();
  const h = useHomeworkLabels();
  const { locale } = useLocale();
  return (
    <section aria-labelledby="feedback-summary">
      <h2 id="feedback-summary">{t.summaryHeading}</h2>
      {topic === null ? null : <p className="feedback-topic"><UntrustedText text={topic} /></p>}
      <ul className="plain-list sheet-list">
        {summaries.map((summary) => (
          <li key={summary.question ?? ''} className="sheet-card">
            <h3>{questionName(summary.question, t, h.questionWord)}</h3>
            {summary.hintOnly !== true && fixedEntry(fixed, summary.question) !== undefined ? <FixedMark entry={fixedEntry(fixed, summary.question) as FixedEntry} /> : null}
            {summary.proved.map((proof) => (
              <p key={proof.text} className="feedback-pill" data-mark="proved">
                <span aria-hidden="true">{MARK_SYMBOLS.proved}</span> {fillTemplate(t.provedOn, { pages: t.pagesPhrase(proof.pages), text: <bdi dir="auto">{localizedNoteSentence(proof.text, locale)}</bdi> })}
              </p>
            ))}
            {summary.gaps.map((gap) => (
              <p key={gap.text} className="feedback-pill" data-mark="refuted">
                <span aria-hidden="true">{MARK_SYMBOLS.refuted}</span> {fillTemplate(t.refutedOn, { pages: t.pagesPhrase(gap.pages), text: <bdi dir="auto">{localizedNoteSentence(gap.text, locale)}</bdi> })}
              </p>
            ))}
            {summary.notCheckedPages.length > 0 ? (
              <p className="feedback-pill" data-mark="read_not_checked">
                <span aria-hidden="true">{MARK_SYMBOLS.read_not_checked}</span> {t.notCheckedOn(t.pagesPhrase(summary.notCheckedPages), summary.needsCourseRule)}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The per-question table, last: every note that shows lines, by question, with its page and mark. */
function QuestionTable({
  entries,
  pageNumberOf,
}: {
  readonly entries: readonly { number: number; note: FeedbackNote }[];
  readonly pageNumberOf: (pageId: string) => number;
}): React.JSX.Element {
  const t = useFeedbackLabels();
  // On a phone the pins count per page and skip lines only read (`feedback-frames.tsx`), so this sheet-wide number would name a different note.
  const phone = useIsPhone();
  const order: (string | null)[] = [];
  for (const { note } of entries) if (!order.includes(note.questionLabel)) order.push(note.questionLabel);
  order.sort((a, b) => (a === null ? 1 : 0) - (b === null ? 1 : 0));
  return (
    <section aria-labelledby="feedback-by-question">
      <h2 id="feedback-by-question">{t.byQuestionHeading}</h2>
      <table className="feedback-table">
        <thead>
          <tr>
            <th scope="col">{t.columnQuestion}</th>
            {phone ? null : <th scope="col">{t.columnNote}</th>}
            <th scope="col">{t.columnPage}</th>
            <th scope="col">{t.columnFound}</th>
          </tr>
        </thead>
        <tbody>
          {order.flatMap((question) =>
            entries
              .filter(({ note }) => note.questionLabel === question)
              .map(({ number, note }) => (
                <tr key={`${String(number)}-${note.claimId ?? note.regionIds.join("-")}`}>
                  <td>{question === null ? '—' : <UntrustedText text={question} />}</td>
                  {phone ? null : <td>{number}</td>}
                  <td>{pageNumberOf(note.pageId)}</td>
                  <td>
                    <span aria-hidden="true">{MARK_SYMBOLS[note.mark]}</span> {t.markWord(note.mark)}
                  </td>
                </tr>
              )),
          )}
        </tbody>
      </table>
    </section>
  );
}
